import { useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldCheck, faAngleLeft } from "@fortawesome/pro-light-svg-icons";
import DataTable from "@components/DataTable/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { snakeToText } from "@utils/helperFunctions";
import { useListAccountSnapshots } from "./api";
import { AccountHealthSnapshot, AccountHealthStatus } from "./types";
import { useStyles } from "./styles";

const formatShortDate = (iso: string): string => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const mm = String(m).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${mm}/${dd}/${y}`;
};

const Show = (): JSX.Element => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { slug = "" } = useParams<{ slug: string }>();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { snapshots, accountName, pageMeta, isLoading } =
    useListAccountSnapshots({
      slug,
      page: { number: pageNumber, size: pageSize },
    });

  const statusBadge = (status: AccountHealthStatus) => {
    const className =
      status === "at_risk"
        ? styles.statusAtRisk
        : status === "watch"
        ? styles.statusWatch
        : styles.statusHealthy;
    return <span className={className}>{snakeToText(status)}</span>;
  };

  const columns: ColumnDef<AccountHealthSnapshot>[] = [
    {
      header: "Date",
      cell: (props) => formatShortDate(props.row.original.weekStart),
    },
    {
      header: "Health",
      cell: (props) => statusBadge(props.row.original.status),
    },
    {
      header: "Reason",
      cell: (props) =>
        props.row.original.primaryDriver
          ? snakeToText(props.row.original.primaryDriver)
          : "—",
    },
    {
      header: "Sign In Slope",
      cell: (props) => (
        <span className={styles.numericCell}>
          {props.row.original.signinSlope.toFixed(2)}
        </span>
      ),
    },
    {
      header: "Project Slope",
      cell: (props) => (
        <span className={styles.numericCell}>
          {props.row.original.projectSlope.toFixed(2)}
        </span>
      ),
    },
    {
      header: "Project Ratio",
      cell: (props) => (
        <span className={styles.numericCell}>
          {`${Math.round(100 * props.row.original.projectBaselineRatio)}%`}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Row>
        <Col>
          <span
            className={styles.backLink}
            onClick={() => navigate("/account_health_snapshots")}
          >
            <FontAwesomeIcon icon={faAngleLeft} /> Back to Account Health
          </span>
          <h1 className={styles.title}>
            {accountName
              ? `${accountName} Account Health History`
              : "Account Health History"}
          </h1>
        </Col>
        <hr className={styles.line} />
      </Row>
      <Row>
        <Col>
          <Card className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <FontAwesomeIcon
                  className={styles.cardIcon}
                  icon={faShieldCheck}
                />
                <h2>Weekly Snapshots</h2>
              </div>
            </div>
            <hr className={styles.line} />
            {isLoading ? (
              <ScopeStackSpinner />
            ) : snapshots.length === 0 ? (
              <div className={styles.emptyState}>
                <h3>No snapshots for this account</h3>
                <p>
                  Once weekly snapshots have been generated for this account,
                  they will appear here.
                </p>
              </div>
            ) : (
              <DataTable
                data={snapshots}
                columns={columns}
                striped
                bordered
                totalPages={pageMeta.pageCount}
                totalRows={pageMeta.recordCount}
                currentPage={pageNumber}
                setCurrentPage={setPageNumber}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                paginationEnabled
                pageSizeEnabled
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Show;
